import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface InventoryFacets {
  years: number[];
  makes: string[];
  manufacturers: string[];
  rvTypes: string[];
}

/** Pull distinct facet values from current available inventory. */
export function useInventoryFacets() {
  return useQuery<InventoryFacets>({
    queryKey: ["inventory-facets"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rv_inventory")
        .select("year, make, manufacturer, rv_type")
        .in("status", ["available", "pending", "coming_soon"])
        .limit(5000);
      if (error) throw error;
      const yearSet = new Set<number>();
      // Dedupe makes/manufacturers/types case-insensitively, preferring the
      // variant with the most lowercase letters (proper case beats UPPER).
      const dedupe = new Map<string, Map<string, string>>([
        ["make", new Map()],
        ["mfr", new Map()],
        ["type", new Map()],
      ]);
      const add = (key: string, raw: string) => {
        const bucket = dedupe.get(key)!;
        const k = raw.trim().toLowerCase();
        if (!k) return;
        const existing = bucket.get(k);
        if (!existing) {
          bucket.set(k, raw.trim());
          return;
        }
        const lowerCount = (s: string) => (s.match(/[a-z]/g) ?? []).length;
        if (lowerCount(raw) > lowerCount(existing)) bucket.set(k, raw.trim());
      };
      for (const row of data ?? []) {
        const r = row as any;
        if (typeof r.year === "number") yearSet.add(r.year);
        if (r.make) add("make", String(r.make));
        if (r.manufacturer) add("mfr", String(r.manufacturer));
        if (r.rv_type) add("type", String(r.rv_type));
      }
      const values = (key: string) =>
        Array.from(dedupe.get(key)!.values()).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" }),
        );
      return {
        years: Array.from(yearSet).sort((a, b) => b - a),
        makes: values("make"),
        manufacturers: values("mfr"),
        rvTypes: values("type"),
      };
    },
  });
}